import { useState, useEffect } from 'react';
import { Button, CoordioDropdown, FormField, Input, Switch, Textarea } from '@coordio/ui';
import { InlineErrorPanel } from '@coordio/ui/layout';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import type { AgentParameterDto, CreateAgentParameterRequest, UpdateAgentParameterRequest } from '../../types/agent';

interface ParameterFormProps {
  parameter: AgentParameterDto | null;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAgentParameterRequest | UpdateAgentParameterRequest) => void;
}

const PARAMETER_TYPES = ['string', 'number', 'boolean', 'json'];

/**
 * Parameter create/edit dialog — uses CortexDialog + FormField for coordio-ui foundation.
 */
export const ParameterForm = ({ parameter, open, onClose, onSubmit }: ParameterFormProps) => {
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('string');
  const [defaultValue, setDefaultValue] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isSecret, setIsSecret] = useState(false);
  const [validationJson, setValidationJson] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (parameter) {
      setKey(parameter.key);
      setLabel(parameter.label);
      setDescription(parameter.description || '');
      setType(parameter.type);
      setDefaultValue(parameter.isSecret ? '' : parameter.defaultValue || '');
      setIsRequired(parameter.isRequired);
      setIsSecret(parameter.isSecret);
      setValidationJson(parameter.validationJson || '');
    } else {
      setKey('');
      setLabel('');
      setDescription('');
      setType('string');
      setDefaultValue('');
      setIsRequired(false);
      setIsSecret(false);
      setValidationJson('');
    }
    setError(null);
  }, [parameter, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);

    if (!key.trim()) {
      setError('Key is required');
      return;
    }

    if (!label.trim()) {
      setError('Label is required');
      return;
    }

    if (validationJson) {
      try {
        JSON.parse(validationJson);
      } catch {
        setError('Validation JSON must be valid JSON');
        return;
      }
    }

    const data: CreateAgentParameterRequest | UpdateAgentParameterRequest = {
      ...(parameter ? { id: parameter.id } : {}),
      key: key.trim(),
      label: label.trim(),
      description: description.trim() || undefined,
      type,
      defaultValue: defaultValue.trim() || undefined,
      isRequired,
      isSecret,
      validationJson: validationJson.trim() || undefined,
    };

    onSubmit(data);
  };

  return (
    <CortexDialog
      open={open}
      onClose={onClose}
      title={parameter ? 'Edit Parameter' : 'Add Parameter'}
      size="lg"
      data-testid="parameter-form-dialog"
    >
      <form onSubmit={handleSubmit}>
        <CortexDialogBody>
          {error && (
            <div className="mb-3">
              <InlineErrorPanel
                message={error}
                action={
                  <Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>
                    Dismiss
                  </Button>
                }
              />
            </div>
          )}

          <div className="flex flex-col gap-3">
            <FormField
              label="Key"
              description="Unique identifier for the parameter (cannot be changed after creation)"
              required
            >
              <Input
                value={key}
                onChange={(e) => setKey(e.target.value)}
                required
                disabled={!!parameter}
              />
            </FormField>

            <FormField label="Label" required>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
            </FormField>

            <FormField label="Description">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </FormField>

            <FormField label="Type">
              <CoordioDropdown
                options={PARAMETER_TYPES.map((t) => ({ value: t, label: t }))}
                value={type}
                onChange={(val) => setType(val ?? 'string')}
                placeholder="Select type"
                size="sm"
                clearable={false}
              />
            </FormField>

            <FormField
              label={isSecret ? 'Secret Value' : 'Default Value'}
              description={
                isSecret && parameter?.hasSecretConfigured
                  ? 'Leave blank to keep the current Key Vault secret.'
                  : isSecret
                    ? 'Saved securely in Azure Key Vault.'
                    : undefined
              }
            >
              <Input
                value={defaultValue}
                onChange={(e) => setDefaultValue(e.target.value)}
                type={isSecret ? 'password' : 'text'}
              />
            </FormField>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <Switch checked={isRequired} onCheckedChange={setIsRequired} />
                Required
              </label>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <Switch checked={isSecret} onCheckedChange={setIsSecret} />
                Secret (store in Key Vault)
              </label>
            </div>

            <FormField
              label="Validation JSON"
              description="JSON schema for parameter validation"
            >
              <Textarea
                className="font-mono"
                value={validationJson}
                onChange={(e) => setValidationJson(e.target.value)}
                rows={4}
              />
            </FormField>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="default">
            {parameter ? 'Update' : 'Create'}
          </Button>
        </CortexDialogFooter>
      </form>
    </CortexDialog>
  );
};
